import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Android, Download, Window } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { environmentConfig } from '../../../config/environment';
import { api } from '../../../services/api';

const platformMeta: Record<string, { title: string; icon: React.ReactNode; description: string }> = {
  windows: {
    title: 'Windows client',
    icon: <Window fontSize="small" />,
    description: 'Electron web-player client. При запуске проверяет автозагрузку Windows и включает её обратно, если выключена.',
  },
  android: {
    title: 'Android APK',
    icon: <Android fontSize="small" />,
    description: 'Native Android/TV player на web-player API с кешированием, командами, sync playback и ручным override расписания.',
  },
};

const downloadUrl = (platform: string) => `${environmentConfig.apiBaseUrl}/api/v1/client-downloads/${platform}`;

export const ClientDownloadsTab: React.FC = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['client-downloads'],
    queryFn: () => api.getClientDownloads(),
    staleTime: 60000,
  });

  const downloads = data?.items || [];

  return (
    <Box>
      <Stack sx={{ mb: 2 }}>
        <Typography variant="h6">Клиентские приложения</Typography>
        <Typography variant="body2" color="text.secondary">
          Актуальные собранные файлы для установки плееров на устройства.
        </Typography>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Не удалось получить список клиентских файлов: {error.message}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Платформа</TableCell>
              <TableCell>Версия</TableCell>
              <TableCell>Файл</TableCell>
              <TableCell>Назначение</TableCell>
              <TableCell align="right">Скачать</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">Загрузка списка файлов...</Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : downloads.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary">
                    Нет опубликованных клиентских сборок.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : downloads.map((item) => {
              const meta = platformMeta[item.platform] || {
                title: item.title || item.platform,
                icon: <Download fontSize="small" />,
                description: item.description || 'Клиентское приложение',
              };

              return (
                <TableRow key={item.platform}>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {meta.icon}
                      <Typography variant="body2" fontWeight={600}>{item.title || meta.title}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={item.version || 'current'} variant="outlined" />
                  </TableCell>
                  <TableCell>{item.filename}</TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{item.description || meta.description}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Download />}
                      href={item.download_url?.startsWith('/api/')
                        ? `${environmentConfig.apiBaseUrl}${item.download_url}`
                        : downloadUrl(item.platform)}
                      download={item.filename}
                    >
                      Скачать
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ClientDownloadsTab;
